// Phase 123 Plan 06 Task 1 — Frontend Sentry facade.
//
// One place that wires the @sentry/react init (service + severity tags,
// environment, release, session-replay PII masking preserved from Phase 71)
// plus exposes:
//   - initSentry()            — called from ./instrument.ts at first line of main
//   - setUser(user | null)    — wired from WorkOS session hook (Pattern 5)
//   - captureFrontendError()  — ErrorBoundary + unhandled capture with severity tagging
//   - captureException()      — back-compat surface for the existing AppErrorBoundary
//
// Contract:
//   • service tag pinned to 'frontend' post-init (RESEARCH §Sentry project structure)
//   • AppError instances → severity taken from the class static; Sentry level from
//     severityToLevel mapping
//   • raw Error / unknown throws → wrapped semantically as SEV2 (UnexpectedError bucket
//     on the frontend — not blocking a human for UI renders; ErrorBoundary path explicitly
//     marks its capture as SEV1 via captureRender())
//   • HTTP 5xx on the frontend is NOT captured here — backend owns it (dedup rule)
//   • Phase 71 session replay config PRESERVED:
//       maskAllText: true, maskAllInputs: true, blockAllMedia: false,
//       replaysSessionSampleRate: 0.1, replaysOnErrorSampleRate: 1.0
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router';
// Use the browser-safe sub-path export — the barrel `@hookmyapp/observability`
// re-exports @sentry/node-dependent modules (init/node, trace/*, logger/adapter)
// which Vite would pull into the browser bundle as Node-externalized stubs.
// `/errors` exposes ONLY the error taxonomy + manifest + severity helpers.
import { AppError, severityToLevel } from '@hookmyapp/observability/errors';
import { isCardLinkPath, stripCardToken } from './strip-card-token';

let initialized = false;

/**
 * Build-time sample-rate knob (VITE_SENTRY_TRACES_SAMPLE_RATE). Invalid,
 * unset, or empty values fall back — Number('') === 0 would silently
 * disable tracing, and out-of-range values make Sentry drop all traces.
 */
export function parseSampleRate(raw: string | undefined, fallback = 0.1): number {
  if (raw === undefined || raw.trim() === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback;
}

export function initSentry(): void {
  if (initialized) return;
  // Phase quick-260426-x4h Task B — gate on VITE_IS_LOCAL_DEV instead of the
  // Vite-build-mode flag (`import.meta.env.PROD`) so the same env var that
  // disables backend Sentry also disables frontend Sentry. Set to 1 in
  // .env.local; absent / 0 in staging/prod builds. Operator can override
  // per-session for instrumentation testing.
  if (import.meta.env.VITE_IS_LOCAL_DEV === '1' || !import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'production',
    release: import.meta.env.VITE_SENTRY_RELEASE,
    enableLogs: true,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // AIT-505 — never start Session Replay on the public /card add-card
      // link. The card-link token lives in the URL fragment for the first
      // instant of the page's life (before its own history.replaceState
      // runs); Replay's DOM/network recording could capture that instant.
      // Gating at init (never constructing the integration for this route)
      // is simpler and stronger than trying to redact it from a running
      // Replay session. Codex round 6 (P1) — `isCardLinkPath` normalizes
      // trailing slashes so `/card/` (same page, different URL) still gates
      // the same as `/card`, mirroring the FirstPromoter gate in main.tsx.
      ...(!isCardLinkPath(window.location.pathname)
        ? [
            Sentry.replayIntegration({
              maskAllText: true,
              maskAllInputs: true,
              blockAllMedia: false,
              networkDetailAllowUrls: [/.*/],
              networkRequestHeaders: ['content-type', 'content-length', 'accept'],
              networkResponseHeaders: ['content-type', 'content-length', 'accept'],
            }),
          ]
        : []),
    ],
    // AIT-505 — defense in depth: strip any URL fragment Sentry still
    // captured (request.url / transaction name derive from
    // window.location.href) so a card-link token can never reach an event
    // payload, even one captured before this page's own replaceState ran.
    beforeSend(event) {
      if (event.request?.url) event.request.url = stripCardToken(event.request.url);
      if (event.transaction) event.transaction = stripCardToken(event.transaction) ?? event.transaction;
      return event;
    },
    beforeSendTransaction(event) {
      if (event.request?.url) event.request.url = stripCardToken(event.request.url);
      if (event.transaction) event.transaction = stripCardToken(event.transaction) ?? event.transaction;
      return event;
    },
    // AIT-505 (review) — beforeSend/beforeSendTransaction never touch
    // event.breadcrumbs. The page's OWN history.replaceState call fires
    // Sentry's history instrumentation before the URL actually changes; on
    // that first history call `lastHref` is unset, so the navigation
    // breadcrumb's `data.from` falls back to the current (still-tokened)
    // location — carrying the card-link token straight into a breadcrumb.
    beforeBreadcrumb(crumb) {
      if (typeof crumb.data?.from === 'string') crumb.data.from = stripCardToken(crumb.data.from);
      if (typeof crumb.data?.to === 'string') crumb.data.to = stripCardToken(crumb.data.to);
      return crumb;
    },
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    // Propagate sentry-trace/baggage ONLY to our API origin. Omitted when
    // VITE_API_URL is unset (local dev, same-origin fetches — SDK default
    // covers same-origin). Never pass an empty string: it matches everything.
    // String targets are SUBSTRING-matched by Sentry; acceptable here because
    // our origin appearing mid-URL in a third-party request is not a realistic
    // shape for this app's fetches.
    ...(import.meta.env.VITE_API_URL
      ? { tracePropagationTargets: [import.meta.env.VITE_API_URL] }
      : {}),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
  Sentry.setTag('service', 'frontend');
  initialized = true;
}

export function setUser(user: { id: string; email?: string | null } | null): void {
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email ?? undefined });
    // Mirror onto tags: Slack alert rules render tags (user.email), not the
    // user context — without this, frontend alerts name nobody.
    Sentry.setTag('user.id', user.id);
    Sentry.setTag('user.email', user.email ?? undefined);
  } else {
    Sentry.setUser(null);
    Sentry.setTag('user.id', undefined);
    Sentry.setTag('user.email', undefined);
  }
}

/**
 * Capture an error from anywhere on the frontend, tagging severity + service
 * consistently. AppError instances drive severity from the class static;
 * raw Errors default to SEV2 (UnexpectedError bucket) so they aren't silently
 * bumped to SEV1.
 */
export function captureFrontendError(
  err: unknown,
  extra?: Record<string, unknown>,
): void {
  const isApp = err instanceof AppError;
  const severity = isApp ? err.severity : 'sev2';
  const level = severityToLevel(severity);
  const code = isApp ? err.code : 'FRONTEND_UNCATEGORIZED';

  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag('service', 'frontend');
    scope.setTag('severity', severity);
    scope.setTag('code', code);
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        scope.setExtra(k, v);
      }
    }
    Sentry.captureException(err);
  });
}

/**
 * React ErrorBoundary path: render errors = SEV1 (a human is waiting for the
 * UI). Separate from captureFrontendError() so we don't misclassify the
 * "Something went wrong" screen.
 */
export function captureRender(
  err: unknown,
  info?: { componentStack?: string },
): void {
  Sentry.withScope((scope) => {
    scope.setLevel('fatal');
    scope.setTag('service', 'frontend');
    scope.setTag('severity', 'sev1');
    scope.setTag('code', 'FRONTEND_RENDER');
    if (info?.componentStack) scope.setExtra('componentStack', info.componentStack);
    Sentry.captureException(err);
  });
}

/**
 * Back-compat alias used by the existing AppErrorBoundary + other callers
 * that were wired before Plan 06. Preserves old `captureException(err, {...})`
 * ergonomics while flowing through captureRender's SEV1 tagging for the
 * ErrorBoundary path. For non-render captures, prefer captureFrontendError().
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  captureRender(
    error,
    typeof context?.componentStack === 'string'
      ? { componentStack: context.componentStack }
      : undefined,
  );
}
